import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/Badge/Badge';

export interface MatchScoreProps {
  /** Match score percentage (0-100) */
  score: number;
  /** Optional reasoning text explaining the match */
  reasoning?: string;
  /** Optional recommendation level */
  recommendation?: 'highly_recommended' | 'recommended' | 'consider' | string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * MatchScore - A standardized component for displaying AI match scores
 * 
 * Features:
 * - Displays match percentage as a badge
 * - Shows recommendation level
 * - Optional reasoning text
 * - Styled container with primary colors
 * 
 * @example
 * ```tsx
 * <MatchScore
 *   score={85}
 *   recommendation="highly_recommended"
 *   reasoning="This venue matches your capacity requirements and is within budget."
 * />
 * ```
 */
export function MatchScore({
  score,
  reasoning,
  recommendation,
  className,
}: MatchScoreProps) {
  const getRecommendationVariant = (rec?: string) => {
    switch (rec) {
      case 'highly_recommended':
        return 'success';
      case 'recommended':
        return 'default';
      case 'consider':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const formatRecommendation = (rec?: string) => {
    if (!rec) return '';
    return rec.replace(/_/g, ' ');
  };

  return (
    <div className={cn('p-2.5 bg-blue-50/50 rounded-md border border-blue-200/50', className)}>
      <div className="flex items-center justify-between mb-1 gap-2">
        <Badge variant="default" className="text-sm font-medium bg-blue-100 text-blue-700 border-blue-200">
          {Math.round(score)}% match
        </Badge>
        {recommendation && (
          <Badge variant={getRecommendationVariant(recommendation)} className="text-xs">
            {formatRecommendation(recommendation)}
          </Badge>
        )}
      </div>
      {reasoning && (
        <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
          {reasoning}
        </p>
      )}
    </div>
  );
}

